import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import api from '../utils/api';
import { Wallet } from 'lucide-react';

const COUNTRIES = [
  { value: 'Nigeria', label: 'Nigeria (NGN)' },
  { value: 'United States', label: 'United States (USD)' },
  { value: 'United Kingdom', label: 'United Kingdom (GBP)' },
  { value: 'United Arab Emirates', label: 'United Arab Emirates (AED)' },
  { value: 'China', label: 'China (CNY)' },
  { value: 'Saudi Arabia', label: 'Saudi Arabia (SAR)' },
];

const RegisterStep2 = () => {
  const [formData, setFormData] = useState({
    first_name: '',
    surname: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    country: '',
    phone_number: ''
  });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleCountryChange = (value) => {
    setFormData({ ...formData, country: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.country) {
      toast.error('Please select your country');
      return;
    }
    setLoading(true);
    try {
      await api.post('/auth/register-details', formData);
      toast.success('Registration complete! Welcome to SamaFlux!');
      navigate('/dashboard');
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (Array.isArray(detail)) {
        toast.error(detail.map(e => e.msg?.replace('Value error, ', '')).join('. '));
      } else {
        toast.error(detail || 'Failed to complete registration');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12 bg-muted" data-testid="register-step2-page">
      <div className="w-full max-w-2xl">
        <Card className="border border-border/50 shadow-xl" data-testid="register-details-card">
          <CardHeader className="text-center">
            <div className="flex items-center justify-center gap-2 mb-4">
              <Wallet className="h-8 w-8 text-primary" />
              <span className="text-2xl font-bold text-primary" style={{fontFamily: 'DM Sans'}}>SamaFlux</span>
            </div>
            <CardTitle className="text-2xl" style={{fontFamily: 'DM Sans'}} data-testid="card-title">Complete Your Profile</CardTitle>
            <CardDescription data-testid="card-description">Step 2 of 2: Personal Information</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="first_name">First Name</Label>
                  <Input id="first_name" name="first_name" value={formData.first_name} onChange={handleChange} required data-testid="first-name-input" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="surname">Surname</Label>
                  <Input id="surname" name="surname" value={formData.surname} onChange={handleChange} required data-testid="surname-input" />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="address">Home Address</Label>
                <Input id="address" name="address" value={formData.address} onChange={handleChange} required data-testid="address-input" />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="city">City</Label>
                  <Input id="city" name="city" value={formData.city} onChange={handleChange} required data-testid="city-input" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="state">State</Label>
                  <Input id="state" name="state" value={formData.state} onChange={handleChange} required data-testid="state-input" />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="zip_code">Zip Code</Label>
                  <Input id="zip_code" name="zip_code" value={formData.zip_code} onChange={handleChange} required data-testid="zip-code-input" />
                </div>
                <div className="space-y-2">
                  <Label>Country</Label>
                  <Select value={formData.country} onValueChange={handleCountryChange} data-testid="country-select">
                    <SelectTrigger data-testid="country-input">
                      <SelectValue placeholder="Select your country" />
                    </SelectTrigger>
                    <SelectContent>
                      {COUNTRIES.map((c) => (
                        <SelectItem key={c.value} value={c.value} data-testid={`country-option-${c.value.toLowerCase().replace(/ /g, '-')}`}>
                          {c.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">Your wallet currency is set based on your country</p>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone_number">Phone Number</Label>
                <Input id="phone_number" name="phone_number" type="tel" value={formData.phone_number} onChange={handleChange} required data-testid="phone-input" />
              </div>

              <Button type="submit" className="w-full rounded-full bg-primary hover:bg-primary/90 payment-button" disabled={loading} data-testid="submit-button">
                {loading ? 'Completing Registration...' : 'Complete Registration'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default RegisterStep2;
